/**
 * BatchEconomicsCard
 *
 * Shows batch-level economics for a substrate log purchase:
 * plan summary, progress tracking, unit economics, and break-even chart.
 * The "you are here" view for a multi-month investment programme.
 */

import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import { Target, TrendingUp, MapPin } from 'lucide-react'

function formatCurrency(value) {
  if (Math.abs(value) >= 1000) {
    return `£${(value / 1000).toFixed(1)}k`
  }
  return `£${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-neutral-900 mb-1">Week {label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-medium">£{Number(entry.value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span>
        </div>
      ))}
    </div>
  )
}

function ProgressBar({ label, actual, target, unit = '', colour = 'bg-blue-500' }) {
  const pct = target > 0 ? Math.min(100, (actual / target) * 100) : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{actual.toLocaleString()}{unit} / {target.toLocaleString()}{unit}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div className={`h-2 rounded-full transition-all ${colour}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export default function BatchEconomicsCard({
  purchase, inductions, overheads, runningCosts, costShares, avgPricePerKg, weeklyBurnRate
}) {
  const economics = useMemo(() => {
    if (!purchase?.target_yield_percent) return null

    const targetBE = Number(purchase.target_yield_percent) / 100
    const unitWeight = Number(purchase.unit_weight_kg)
    const totalLogs = purchase.quantity
    const costPerLog = Number(purchase.cost_per_log)
    const batchSize = purchase.induction_batch_size || 70
    const avgPrice = avgPricePerKg || 16.30

    // Partner share — determines what portion of logs/yield goes to partners vs GMCo
    // Use the max cost-share ratio as a proxy for log allocation (e.g. 50% container share = 50% of logs)
    const partnerLogShare = costShares.length > 0
      ? Math.max(...costShares.filter(cs => cs.overhead_category === 'container_rental').map(cs => Number(cs.share_ratio)), 0)
      : 0
    const gmcoLogShare = 1 - partnerLogShare
    const gmcoLogs = Math.round(totalLogs * gmcoLogShare)

    // Plan targets — scoped to GMCo's share
    const expectedYieldPerLog = unitWeight * targetBE
    const expectedTotalYield = gmcoLogs * expectedYieldPerLog
    const totalBatches = Math.ceil(totalLogs / batchSize) // still total batches (GMCo + partner)
    const totalLogCost = Number(purchase.total_cost) // GMCo paid for all logs upfront
    const projectedRevenue = expectedTotalYield * avgPrice

    // Programme duration: derived from actual burn rate (logs depleted per week)
    // Burn rate includes both inductions and shipments — real observed cadence
    const effectiveBurnRate = weeklyBurnRate > 0 ? weeklyBurnRate : totalLogs / 58 // fallback
    const weeksToDeplete = purchase.logs_remaining / effectiveBurnRate
    const purchaseDate = new Date(purchase.purchase_date + 'T00:00:00')
    const now = new Date()
    const weeksSoFar = Math.max(1, (now - purchaseDate) / (7 * 24 * 60 * 60 * 1000))
    const currentWeek = Math.round(weeksSoFar)
    const programmeWeeks = Math.round(currentWeek + weeksToDeplete)

    // Overhead allocation (GMCo's share)
    const monthlyOverhead = overheads.length > 0
      ? overheads.reduce((sum, o) => sum + Number(o.amount || 0), 0) / Math.max(1, new Set(overheads.map(o => o.period_start?.substring(0, 7))).size)
      : 450
    const gmcoOverheadShare = costShares.length > 0
      ? costShares
          .filter(cs => cs.overhead_category === 'container_rental')
          .reduce((_, cs) => 1 - Number(cs.share_ratio), 0.5)
      : 0.5
    const weeklyOverheadGMCo = (monthlyOverhead * gmcoOverheadShare) / 4.33
    const totalOverheadCost = weeklyOverheadGMCo * programmeWeeks

    // Running costs for this purchase (actual to date)
    const purchaseRunningCosts = (runningCosts || [])
      .filter(rc => rc.purchase_id === purchase.id)
      .reduce((sum, rc) => sum + Number(rc.amount || 0), 0)
    // Weekly running cost rate (for projections)
    const weeklyRunningCostRate = weeksSoFar > 0 ? purchaseRunningCosts / weeksSoFar : 0

    // GMCo's share of log cost (paid upfront, partner reimburses their share)
    const gmcoLogCost = totalLogCost * gmcoLogShare

    // Total projected costs — GMCo's share only (log cost + overhead + running costs)
    const projectedRunningCosts = weeklyRunningCostRate * programmeWeeks
    const projectedCosts = gmcoLogCost + totalOverheadCost + projectedRunningCosts

    // GMCo-only view: costs already reflect GMCo's share, no partner income needed

    // Actuals
    const purchaseInductions = inductions.filter(i => i.purchase_id === purchase.id)
    const logsInducted = purchaseInductions.reduce((sum, i) => sum + i.log_count, 0)
    const totalHarvested = purchaseInductions.reduce((sum, i) => sum + Number(i.total_harvest_kg || 0), 0)
    // BE from producing batches only (fruiting/harvesting/resting — excludes freshly inducted)
    const producingInductions = purchaseInductions.filter(i => i.status !== 'inducted')
    const producingSubstrateWeight = producingInductions.reduce((sum, i) => sum + Number(i.total_substrate_weight_kg || 0), 0)
    const actualBE = producingSubstrateWeight > 0 ? (totalHarvested / producingSubstrateWeight) * 100 : 0
    const actualRevenue = totalHarvested * avgPrice

    // Observed weekly revenue rate (from actual harvests)
    const weeklyRevenueRate = weeksSoFar > 0 ? actualRevenue / weeksSoFar : 0

    // Unit economics — per producing GMCo log (excludes freshly inducted batches)
    const producingLogs = producingInductions.reduce((sum, i) => sum + i.log_count, 0)
    const actualRevenuePerLog = producingLogs > 0 ? actualRevenue / producingLogs : 0
    // Cost per producing log: only their log cost + their share of overhead
    const producingLogCost = producingLogs * costPerLog
    const producingOverhead = weeklyOverheadGMCo * weeksSoFar * (producingLogs / Math.max(1, logsInducted))
    const producingRunning = purchaseRunningCosts * (producingLogs / Math.max(1, logsInducted))
    const actualCostSoFar = producingLogCost + producingOverhead + producingRunning
    const actualCostPerLog = producingLogs > 0 ? actualCostSoFar / producingLogs : 0
    const actualMarginPerLog = actualRevenuePerLog - actualCostPerLog
    const projectedRevenuePerLog = expectedYieldPerLog * avgPrice
    const projectedCostPerLog = gmcoLogs > 0 ? projectedCosts / gmcoLogs : 0
    const projectedMarginPerLog = projectedRevenuePerLog - projectedCostPerLog

    // Break-even chart data — use actuals then project from observed rates
    const chartData = []
    for (let week = 0; week <= programmeWeeks; week++) {
      // Costs: GMCo's share only — log costs step with induction rate, overhead linear
      // GMCo's log cost = logs inducted (not shipped) × cost per log
      const gmcoLogsUsedByWeek = Math.min(gmcoLogs, Math.round(effectiveBurnRate * gmcoLogShare * week))
      const cumLogCost = gmcoLogsUsedByWeek * costPerLog
      const cumOverhead = weeklyOverheadGMCo * week
      const cumRunning = weeklyRunningCostRate * week
      const cumCosts = cumLogCost + cumOverhead + cumRunning

      // Revenue: use actual rate for weeks <= currentWeek, then project
      // Revenue starts when first harvest happens, scales with production pipeline
      let cumRevenue
      if (week <= currentWeek) {
        // Actual: linear interpolation of observed revenue
        cumRevenue = weeklyRevenueRate * week
      } else {
        // Projected: actual revenue so far + projected weekly rate for remaining weeks
        // As more batches enter production, yield rate should increase
        // Model: ramp from current rate to steady-state rate
        const steadyStateWeeklyYield = expectedTotalYield / programmeWeeks
        const steadyStateWeeklyRevenue = steadyStateWeeklyYield * avgPrice
        const weeksAhead = week - currentWeek
        const rampWeeks = Math.min(20, programmeWeeks - currentWeek) // ramp over 20 weeks to steady state
        const rampFraction = Math.min(1, weeksAhead / rampWeeks)
        const blendedRate = weeklyRevenueRate + (steadyStateWeeklyRevenue - weeklyRevenueRate) * rampFraction
        cumRevenue = actualRevenue + blendedRate * weeksAhead
      }

      const netPosition = cumRevenue - cumCosts

      chartData.push({
        week,
        costs: Math.round(cumCosts),
        revenue: Math.round(cumRevenue),
        netPosition: Math.round(netPosition),
        isActual: week <= currentWeek
      })
    }

    // Find break-even week (must have positive net after costs have started accumulating)
    const breakEvenWeek = chartData.find(d => d.netPosition >= 0 && d.week > 1 && d.costs > 0)?.week || null

    const projectedNetPL = projectedRevenue - projectedCosts

    return {
      // Plan
      targetBE: Number(purchase.target_yield_percent),
      expectedTotalYield,
      totalBatches,
      programmeWeeks,
      projectedRevenue,
      projectedCosts,
      projectedNetPL,
      // Actuals
      logsInducted,
      gmcoLogs,
      totalLogs,
      totalHarvested,
      actualBE,
      actualRevenue,
      currentWeek,
      // Unit economics
      actualRevenuePerLog,
      actualCostPerLog,
      actualMarginPerLog,
      projectedRevenuePerLog,
      projectedCostPerLog,
      projectedMarginPerLog,
      // Chart
      chartData,
      breakEvenWeek
    }
  }, [purchase, inductions, overheads, runningCosts, costShares, avgPricePerKg, weeklyBurnRate])

  if (!economics) return null

  const e = economics

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Batch Economics — {purchase.purchase_code}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Target BE</div>
            <div className="text-lg font-bold">{e.targetBE}%</div>
          </div>
          <div>
            <div className="text-muted-foreground">Expected Yield</div>
            <div className="text-lg font-bold">{e.expectedTotalYield.toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg</div>
          </div>
          <div>
            <div className="text-muted-foreground">Induction Batches</div>
            <div className="text-lg font-bold">{e.totalBatches}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Programme Duration</div>
            <div className="text-lg font-bold">~{e.programmeWeeks} weeks</div>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            You Are Here — Week {e.currentWeek}
          </div>
          <ProgressBar
            label="GMCo Logs Inducted"
            actual={e.logsInducted}
            target={e.gmcoLogs}
          />
          <ProgressBar
            label="Kg Harvested"
            actual={Math.round(e.totalHarvested * 10) / 10}
            target={Math.round(e.expectedTotalYield)}
            unit=" kg"
            colour="bg-green-500"
          />
          <ProgressBar
            label="Biological Efficiency"
            actual={Math.round(e.actualBE * 10) / 10}
            target={e.targetBE}
            unit="%"
            colour={e.actualBE >= e.targetBE * 0.8 ? 'bg-green-500' : e.actualBE >= e.targetBE * 0.5 ? 'bg-amber-500' : 'bg-red-500'}
          />
        </div>

        {/* Unit Economics */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 text-sm font-medium mb-3">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Unit Economics (per log)
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div title={`Projected at target BE: £${e.projectedRevenuePerLog.toFixed(2)}/log`}>
              <div className="text-muted-foreground">Revenue / log</div>
              <div className="text-lg font-bold">£{e.actualRevenuePerLog.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">so far · target £{e.projectedRevenuePerLog.toFixed(2)}</div>
            </div>
            <div title={`Projected over full programme: £${e.projectedCostPerLog.toFixed(2)}/log`}>
              <div className="text-muted-foreground">Cost / log</div>
              <div className="text-lg font-bold">£{e.actualCostPerLog.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">so far · projected £{e.projectedCostPerLog.toFixed(2)}</div>
            </div>
            <div title={`Projected margin: ${e.projectedMarginPerLog >= 0 ? '+' : ''}£${e.projectedMarginPerLog.toFixed(2)}/log`}>
              <div className="text-muted-foreground">Margin / log</div>
              <div className={`text-lg font-bold ${e.actualMarginPerLog >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {e.actualMarginPerLog >= 0 ? '+' : ''}£{e.actualMarginPerLog.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground">so far · projected {e.projectedMarginPerLog >= 0 ? '+' : ''}£{e.projectedMarginPerLog.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Financial Forecast Summary */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Projected Revenue</div>
              <div className="font-bold text-green-600">£{e.projectedRevenue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
              <div className="text-xs text-muted-foreground">GMCo's share</div>
            </div>
            <div>
              <div className="text-muted-foreground">Projected Costs</div>
              <div className="font-bold text-red-600">-£{e.projectedCosts.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
              <div className="text-xs text-muted-foreground">GMCo's share</div>
            </div>
            <div>
              <div className="text-muted-foreground">Projected Net P/L</div>
              <div className={`font-bold ${e.projectedNetPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {e.projectedNetPL >= 0 ? '+' : ''}£{e.projectedNetPL.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
              </div>
            </div>
          </div>
        </div>

        {/* Break-even Chart */}
        <div className="border-t pt-4">
          <div className="text-sm font-medium mb-3">Break-even Forecast</div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={e.chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="week"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                label={{ value: 'Weeks', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#6b7280' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="netPosition"
                name="Net Position"
                fill="#dcfce7"
                stroke="none"
                fillOpacity={0.5}
              />
              <Line
                type="monotone"
                dataKey="costs"
                name="Cumulative Costs"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Cumulative Revenue"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
              />
              {/* Break-even line */}
              <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
              {/* You are here */}
              <ReferenceLine
                x={e.currentWeek}
                stroke="#111827"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{ value: 'You are here', position: 'top', fontSize: 11, fill: '#111827' }}
              />
              {/* Break-even point */}
              {e.breakEvenWeek && (
                <ReferenceLine
                  x={e.breakEvenWeek}
                  stroke="#22c55e"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  label={{ value: `Break-even (~wk ${e.breakEvenWeek})`, position: 'top', fontSize: 10, fill: '#22c55e' }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          {e.breakEvenWeek && (
            <div className="text-center text-xs text-muted-foreground mt-2">
              Projected break-even at week {e.breakEvenWeek} (~{Math.round(e.breakEvenWeek / 4.33)} months)
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
